import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';

import React, { createContext, useContext, useEffect, useState } from 'react';

import { useLocalStorage } from '@zapier/common-hooks';
import { useMediaProps } from '@zapier/responsive-utils';

import type { UseQuery } from '../types';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../constants';
import { SplitIOProvider } from './SplitIOProvider';

type UniversalNavContext = {
  canForceSidebarOpen: boolean;
  canTopbarFitBackToAppButton: boolean;
  canTopbarFitLinks: boolean;
  currentAccountId?: number;
  currentUrl: string;
  isLoadingLoggedIn: boolean;
  isLoggedIn: boolean;
  isOnAppPage: boolean;
  isSearchFullWidth: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarOpenedByTopbar: boolean;
  isSidebarRendered: boolean;
  onToggleSidebarVisibility?: () => void;
  setIsSidebarForcedOpen: (state: boolean) => void;
  setIsSidebarOpenedByTopbar: (state: boolean) => void;
  setIsSidebarRendered: (state: boolean) => void;
  shouldRenderSearch: boolean;
  shouldRenderSidebar: boolean;
  shouldRenderTopbarLinks: boolean;
  shouldRenderUniversalNav: boolean;
  sidebarWidth: number;
  topbarSize: 'small' | 'medium';
  topbarHeight: number;
  useQuery: UseQuery;
};

const defaultContext: UniversalNavContext = {
  canForceSidebarOpen: false,
  canTopbarFitBackToAppButton: false,
  canTopbarFitLinks: false,
  currentAccountId: undefined,
  currentUrl: '',
  isLoadingLoggedIn: true,
  isLoggedIn: false,
  isOnAppPage: false,
  isSearchFullWidth: true,
  isSidebarControlledByTopbar: false,
  isSidebarForcedOpen: false,
  isSidebarOpenedByTopbar: false,
  isSidebarRendered: false,
  onToggleSidebarVisibility: () => {},
  setIsSidebarForcedOpen: () => {},
  setIsSidebarOpenedByTopbar: () => {},
  setIsSidebarRendered: () => {},
  shouldRenderSearch: true,
  shouldRenderSidebar: false,
  shouldRenderTopbarLinks: false,
  shouldRenderUniversalNav: true,
  sidebarWidth: 0,
  topbarSize: 'medium',
  topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  // @ts-ignore. This is a default value.
  useQuery: () => {},
};

const UniversalNavContext = createContext(defaultContext);

export const useUniversalNavContext = () => useContext(UniversalNavContext);

type Props = {
  /**
   * What to render within the provider.
   */
  children: React.ReactNode;
  /**
   * `id` of the current account. Will be falsey if user isn't logged in.
   */
  currentAccountId?: number;
  /**
   * Current path in the browser's address bar.
   */
  currentUrl: string;
  /**
   * Hook to actually execute the query. Typically provided by an `@apollo` package.
   */
  useQuery: UseQuery;
};

export const isAuthenticatedQuery: DocumentNode = gql`
  query isAuthenticatedQuery {
    isAuthenticatedV2
  }
`;

const isCurrentUrlAnAuthPage = (currentUrl: string) => {
  return (
    currentUrl.includes('/login') ||
    currentUrl.includes('/sign-up') ||
    currentUrl.includes('/oauth/authorize')
  );
};

const isCurrentUrlAnAppPage = (currentUrl: string) => {
  return currentUrl.includes('/app/');
};

const isCurrentUrlAnOnboardingPage = (currentUrl: string) => {
  return currentUrl.includes('/app/onboarding');
};

const shouldRenderSidebarForRoute = ({
  currentUrl,
}: {
  currentUrl: string;
}) => {
  return (
    isCurrentUrlAnAppPage(currentUrl) &&
    !isCurrentUrlAnAuthPage(currentUrl) &&
    !isCurrentUrlAnOnboardingPage(currentUrl)
  );
};

const shouldRenderTopbarLinksForRoute = ({
  currentUrl,
}: {
  currentUrl: string;
}) => {
  return (
    !isCurrentUrlAnAppPage(currentUrl) && !isCurrentUrlAnAuthPage(currentUrl)
  );
};

const getShouldRenderSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
}) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isSidebarControlledByTopbar) {
    return isSidebarOpenedByTopbar;
  }
  if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl })) {
    return true;
  }
  if (!canTopbarFitLinks && shouldRenderTopbarLinksForRoute({ currentUrl })) {
    return true;
  }
  return false;
};

const getShouldRenderSearch = ({ currentUrl }: { currentUrl: string }) => {
  if (
    isCurrentUrlAnAuthPage(currentUrl) ||
    isCurrentUrlAnOnboardingPage(currentUrl)
  ) {
    return false;
  }
  return true;
};

const getShouldRenderTopbarToggle = ({
  isSidebarControlledByTopbar,
  isLoggedIn,
  currentUrl,
}: {
  isSidebarControlledByTopbar: boolean;
  isLoggedIn: boolean;
  currentUrl: string;
}) => {
  // If the sidebar isn't controlled by the topbar then
  // the toggle should never render, so bail immediately.
  if (!isSidebarControlledByTopbar) {
    return false;
  }
  if (isLoggedIn) {
    return true;
  }
  return shouldRenderTopbarLinksForRoute({
    currentUrl,
  });
};

const getSidebarWidth = ({
  canForceSidebarOpen,
  isSidebarForcedOpen,
  isSidebarRendered,
  isSidebarControlledByTopbar,
}: {
  canForceSidebarOpen: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarRendered: boolean;
  isSidebarControlledByTopbar: boolean;
}) => {
  if (!isSidebarRendered || isSidebarControlledByTopbar) {
    return 0;
  }
  if (canForceSidebarOpen && isSidebarForcedOpen) {
    return UNIVERSAL_SIDEBAR_WIDTH;
  }
  return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};

export const UniversalNavProvider = (props: Props) => {
  const { currentUrl, useQuery } = props;
  const isOnAppPage = isCurrentUrlAnAppPage(currentUrl);
  const {
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    canForceSidebarOpen,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    topbarSize,
    topbarHeight,
  } = useMediaProps({
    '(min-width: 0px)': {
      isSidebarControlledByTopbar: true,
      isSearchFullWidth: true,
      canForceSidebarOpen: false,
      canTopbarFitBackToAppButton: false,
      canTopbarFitLinks: false,
      topbarSize: 'small',
      topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
    },
    '(min-width: 450px)': {
      canTopbarFitBackToAppButton: true,
      topbarSize: 'medium',
      topbarHeight: UNIVERSAL_TOPBAR_HEIGHT,
    },
    '(min-width: 960px)': {
      isSidebarControlledByTopbar: false,
      isSearchFullWidth: false,
      canTopbarFitLinks: true,
    },
    '(min-width: 1280px)': {
      canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl }),
    },
  });
  const shouldRenderTopbarLinks =
    canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({
      currentUrl,
    });
  const authResult = useQuery(isAuthenticatedQuery, {
    // Always recheck whether the user is authenticated.
    // This is especially important when transitioning from
    // being logged out to logged in and vice versa.
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });
  const isLoggedIn = authResult.data && authResult.data.isAuthenticatedV2;
  const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
  const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage(
    'UniversalNav:isSidebarForcedOpen'
  );
  // Default to opening sidebar if the user hasn't chosen yet.
  useEffect(() => {
    if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
      setIsSidebarForcedOpen(true);
    }
  }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const shouldRenderSidebar = getShouldRenderSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
  });
  const shouldRenderSearch = getShouldRenderSearch({
    currentUrl,
  });
  const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
    isSidebarControlledByTopbar,
    isLoggedIn,
    currentUrl,
  });
  const onToggleSidebarVisibility = shouldRenderTopbarToggle
    ? () => {
        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
      }
    : undefined;
  const sidebarWidth = getSidebarWidth({
    canForceSidebarOpen,
    isSidebarForcedOpen,
    isSidebarRendered,
    isSidebarControlledByTopbar,
  });
  const value = {
    currentAccountId: props.currentAccountId,
    isLoadingLoggedIn: authResult.loading,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    canForceSidebarOpen,
    currentUrl,
    isLoggedIn,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isSidebarForcedOpen:
      canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
    isSidebarRendered,
    onToggleSidebarVisibility,
    setIsSidebarOpenedByTopbar,
    setIsSidebarForcedOpen,
    setIsSidebarRendered,
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderTopbarLinks,
    shouldRenderUniversalNav: true,
    sidebarWidth,
    topbarHeight,
    topbarSize,
    useQuery,
  };
  return (
    <UniversalNavContext.Provider value={value}>
      <SplitIOProvider>{props.children}</SplitIOProvider>
    </UniversalNavContext.Provider>
  );
};
